import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { UserRole } from '../types';
import { NotificationBell } from './NotificationBell';
import { WelcomeModal } from './WelcomeModal';

interface MenuItem {
  to: string;
  label: string;
  roles?: UserRole[];
}

interface MenuCategory {
  key: string;
  label: string;
  items: MenuItem[];
}

export const Layout: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['mainMenu', 'finance', 'management', 'platform', 'marketing']);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(c => c !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  // Platform Admin has a separate, simplified menu
  const platformAdminMenuCategories: MenuCategory[] = [
    {
      key: 'platform',
      label: '🖥️ Platform',
      items: [
        { to: '/app/dashboard',        label: 'Dashboard' },
        { to: '/app/organizations',    label: 'Organizasyonlar' },
        { to: '/app/platform/users',   label: 'Kullanıcılar' },
      ],
    },
    {
      key: 'marketing',
      label: '📣 Pazarlama',
      items: [
        { to: '/app/demo-email', label: 'Demo Maili Gönder' },
      ],
    },
  ];

  // Regular users menu (Org Admin, Manager, Staff, etc.)
  const regularMenuCategories: MenuCategory[] = [
    {
      key: 'mainMenu',
      label: '🏠 Ana Menü',
      items: [
        { to: '/app/dashboard', label: 'Dashboard', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER, UserRole.STAFF, UserRole.PROVIDER_MANAGER, UserRole.PROVIDER_USER] },
        { to: '/app/transfers', label: 'Transferler', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER, UserRole.STAFF] },
        { to: '/app/provider/transfers', label: 'Transferlerim', roles: [UserRole.PROVIDER_MANAGER, UserRole.PROVIDER_USER, UserRole.POOL_MEMBER] },
        { to: '/app/form-submissions', label: 'Form Başvuruları', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
      ],
    },
    {
      key: 'payments',
      label: '💰 Ödemeler',
      items: [
        { to: '/app/admin/customer-payments', label: 'Tahsilat (Kaynak Firma)', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
        { to: '/app/admin/provider-payments', label: 'Tedarikçi Ödemeleri', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
        { to: '/app/admin/pool-member-payments', label: 'Havuzcu Ödemeleri', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
        { to: '/app/provider-payments', label: 'Ödemelerim', roles: [UserRole.PROVIDER_MANAGER] },
        { to: '/app/pool/payments', label: 'Ödemelerim', roles: [UserRole.POOL_MEMBER] },
      ],
    },
    {
      key: 'pool',
      label: '🏊 Havuz',
      items: [
        { to: '/app/provider-groups', label: 'Havuz Yönetimi', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
      ],
    },
    {
      key: 'providers',
      label: '🚗 Tedarikçiler',
      items: [
        { to: '/app/providers', label: 'Firmalar', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER, UserRole.STAFF] },
        { to: '/app/drivers', label: 'Sürücüler', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER, UserRole.STAFF, UserRole.PROVIDER_MANAGER, UserRole.PROVIDER_USER] },
        { to: '/app/vehicle-types', label: 'Araç Tipleri', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER, UserRole.PROVIDER_MANAGER, UserRole.PROVIDER_USER] },
      ],
    },
    {
      key: 'management',
      label: '⚙️ Yönetim',
      items: [
        { to: '/app/customers', label: 'Kaynak Firmalar', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
        { to: '/app/pricing', label: 'Fiyatlandırma', roles: [UserRole.ORG_ADMIN, UserRole.MANAGER] },
        { to: '/app/users', label: 'Kullanıcılar', roles: [UserRole.ORG_ADMIN] },
      ],
    },
  ];

  // Select menu based on user role
  const menuCategories = user?.role === UserRole.PLATFORM_ADMIN
    ? platformAdminMenuCategories
    : regularMenuCategories;

  const canViewItem = (item: MenuItem): boolean => {
    if (!item.roles) return true;
    if (!user?.role) return false;
    return item.roles.includes(user.role as UserRole);
  };

  const getVisibleCategories = (): MenuCategory[] => {
    return menuCategories
      .map(category => ({
        ...category,
        items: category.items.filter(canViewItem),
      }))
      .filter(category => category.items.length > 0);
  };

  const visibleCategories = getVisibleCategories();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-full w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-bold text-blue-600 dark:text-blue-400">Transfer Paneli</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 text-gray-500 min-w-[44px] min-h-[44px] flex items-center justify-center text-xl"
            >
              ✕
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {visibleCategories.map((category) => (
              <div key={category.key} className="mb-2">
                <button
                  onClick={() => toggleCategory(category.key)}
                  className="w-full flex items-center justify-between px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-700/50 active:bg-gray-100 min-h-[44px]"
                >
                  <span>{category.label}</span>
                  <span className="text-xs p-1">
                    {expandedCategories.includes(category.key) ? '▼' : '▶'}
                  </span>
                </button>
                {expandedCategories.includes(category.key) && (
                  <div className="mt-1">
                    {category.items.map((item, itemIndex) => (
                      <SidebarLink
                        key={itemIndex}
                        to={item.to}
                        onClick={() => setSidebarOpen(false)}
                      >
                        {item.label}
                      </SidebarLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <Link
              to="/app/profile"
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                  {(user?.fullName || user?.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.fullName || user?.email}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.role}
                </p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center gap-2 px-2 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar for mobile */}
        <header className="bg-white dark:bg-gray-800 shadow-sm lg:hidden">
          <div className="flex items-center justify-between h-14 px-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 text-gray-500 text-2xl min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              ☰
            </button>
            <h1 className="text-lg font-bold text-blue-600 dark:text-blue-400">Transfer Paneli</h1>
            <NotificationBell />
          </div>
        </header>

        {/* Top bar for desktop */}
        <header className="hidden lg:flex bg-white dark:bg-gray-800 shadow-sm h-14 items-center justify-end px-6">
          <NotificationBell />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <WelcomeModal />
    </div>
  );
};

interface SidebarLinkProps {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
        isActive
          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600'
      }`}
    >
      {children}
    </Link>
  );
};
